import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, Bug, ChevronDown, Home, RefreshCw } from 'lucide-react';

export interface ErrorBoundaryFallbackProps {
  error: Error;
  errorInfo: ErrorInfo | null;
  errorId: string;
  resetErrorBoundary: () => void;
  reloadPage: () => void;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((props: ErrorBoundaryFallbackProps) => ReactNode);
  title?: string;
  description?: string;
  resetKeys?: readonly unknown[];
  isolate?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

const DEFAULT_TITLE = 'Something went wrong';
const DEFAULT_DESCRIPTION =
  'The visualization recovered into a protected state. You can retry this view or reload the application.';

function createErrorId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).slice(2, 8);
  return `err_${timestamp}_${random}`;
}

function resetKeysChanged(
  previousKeys: readonly unknown[] | undefined,
  nextKeys: readonly unknown[] | undefined,
): boolean {
  if (previousKeys === nextKeys) {
    return false;
  }

  if (!previousKeys || !nextKeys) {
    return Boolean(previousKeys || nextKeys);
  }

  if (previousKeys.length !== nextKeys.length) {
    return true;
  }

  return previousKeys.some((key, index) => !Object.is(key, nextKeys[index]));
}

function formatError(error: Error): string {
  const name = error.name || 'Error';
  const message = error.message || 'An unknown rendering error occurred.';
  return `${name}: ${message}`;
}

function reloadPage(): void {
  window.location.reload();
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = {
    error: null,
    errorInfo: null,
    errorId: '',
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      error,
      errorId: createErrorId(),
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    this.logError(error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  override componentDidUpdate(previousProps: ErrorBoundaryProps): void {
    if (
      this.state.error &&
      resetKeysChanged(previousProps.resetKeys, this.props.resetKeys)
    ) {
      this.resetErrorBoundary();
    }
  }

  private logError(error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.groupCollapsed(`[ErrorBoundary] ${formatError(error)}`);
      console.error(error);
      console.error(errorInfo.componentStack);
      console.groupEnd();
      return;
    }

    console.error('[ErrorBoundary]', {
      errorId: this.state.errorId,
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  private resetErrorBoundary = (): void => {
    this.props.onReset?.();
    this.setState({
      error: null,
      errorInfo: null,
      errorId: '',
    });
  };

  private renderFallback(): ReactNode {
    const { error, errorInfo, errorId } = this.state;

    if (!error) {
      return null;
    }

    if (typeof this.props.fallback === 'function') {
      return this.props.fallback({
        error,
        errorInfo,
        errorId,
        resetErrorBoundary: this.resetErrorBoundary,
        reloadPage,
      });
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const containerClassName = this.props.isolate
      ? 'flex h-full min-h-[320px] w-full items-center justify-center bg-slate-950/70 p-4'
      : 'flex min-h-screen w-screen items-center justify-center bg-slate-950 p-4';

    return (
      <div className={containerClassName} role="alert" aria-live="assertive">
        <section className="w-full max-w-lg rounded-lg border border-red-500/30 bg-slate-900 p-6 shadow-2xl shadow-red-950/30">
          <div className="mb-5 flex items-start gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-red-500/30 bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-300" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-slate-50">
                {this.props.title ?? DEFAULT_TITLE}
              </h1>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                {this.props.description ?? DEFAULT_DESCRIPTION}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-slate-700/70 bg-slate-950/70 p-4">
            <div className="flex items-start gap-3">
              <Bug className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-300" aria-hidden="true" />
              <div className="min-w-0">
                <p className="break-words font-mono text-xs leading-5 text-red-200">
                  {formatError(error)}
                </p>
                {errorId && (
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-wide text-slate-500">
                    Reference {errorId}
                  </p>
                )}
              </div>
            </div>

            {import.meta.env.DEV && errorInfo?.componentStack && (
              <details className="mt-4 group">
                <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-medium text-slate-400 transition-colors hover:text-slate-200">
                  <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" aria-hidden="true" />
                  Component stack
                </summary>
                <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-950 p-3 text-xs leading-5 text-slate-400">
                  {errorInfo.componentStack.trim()}
                </pre>
              </details>
            )}
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={this.resetErrorBoundary}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-cyan-950/30 transition-colors hover:bg-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Retry
            </button>
            <button
              type="button"
              onClick={reloadPage}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm font-semibold text-slate-100 transition-colors hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              <Home className="h-4 w-4" aria-hidden="true" />
              Reload
            </button>
          </div>
        </section>
      </div>
    );
  }

  override render(): ReactNode {
    if (this.state.error) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
